import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import useTranslation from "@/hooks/use_translation";
import SearchIcon from "@/sprout/icons/search";
import { TextInput } from "@/sprout/text_input";
import { IconLocationInput, type Location } from "@components/ui/location_input";
import { Popover, PopoverAnchor, PopoverContent } from "@components/ui/popover";
import { RecentSearchesDropdown } from "./recent_searches_dropdown";
import { type RecentSearch } from "@/types/job_search/recent_searches";
import "./search_bar.scss";
import { PrimaryButton } from "@sprout/buttons";

export type Props = {
  onSearch: () => void;
  query: string;
  onQueryChange: (query: string) => void;
  location: Location | null;
  onLocationChange: (location: Location | null) => void;
  disabled: boolean;
  recentSearches: RecentSearch[];
  onRecentSearchSelect?: (search: RecentSearch) => void;
};

export const SearchBar = ({
  onSearch,
  query,
  onQueryChange,
  location,
  onLocationChange,
  disabled,
  recentSearches,
  onRecentSearchSelect,
}: Props) => {
  const { t } = useTranslation("jobSearch");

  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const [dropdownWidth, setDropdownWidth] = useState<number | null>(null);

  const hasRecentSearches = !!recentSearches?.length && !!onRecentSearchSelect;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey && event.key === "k") inputRef.current?.focus();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const anchor = anchorRef.current;
    const parent = anchor?.parentElement;
    if (!anchor || !parent) return;

    const update = () => {
      setDropdownWidth(parent.offsetWidth >= 500 ? 500 : anchor.offsetWidth);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(anchor);
    observer.observe(parent);
    return () => observer.disconnect();
  }, []);

  const handleEnterKey = (event: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Enter") onSearch();
  };

  const handleRecentSelect = (search: RecentSearch) => {
    setShowRecentSearches(false);
    onRecentSearchSelect?.(search);
  };

  return (
    <div className="search-bar">
      <Popover open={showRecentSearches && hasRecentSearches} onOpenChange={setShowRecentSearches}>
        <PopoverAnchor asChild>
          <div
            ref={anchorRef}
            onFocusCapture={() => hasRecentSearches && setShowRecentSearches(true)}
          >
            <TextInput
              ref={inputRef}
              placeholder={t("searchPlaceholder")}
              onChange={(e) => onQueryChange(e.currentTarget.value || "")}
              onKeyDown={handleEnterKey}
              maxLength={255}
              value={query}
              narrow={true}
              icon={<SearchIcon />}
              onClear={() => onQueryChange("")}
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          sideOffset={0}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            if (anchorRef.current?.contains(event.target as Node)) {
              event.preventDefault();
            }
          }}
          className="recent-searches-popover-shell"
          style={{ width: dropdownWidth ?? undefined }}
        >
          <RecentSearchesDropdown recentSearches={recentSearches} onSelect={handleRecentSelect} />
        </PopoverContent>
      </Popover>
      <div>
        <IconLocationInput onChange={onLocationChange} value={location} granular />
      </div>
      <PrimaryButton onClick={onSearch} disabled={disabled}>
        {t("search")}
      </PrimaryButton>
    </div>
  );
};
